import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  Star,
  MessageSquare,
  AlertCircle,
  ThumbsUp,
  Bug,
  Lightbulb,
  Clock,
  CheckCircle,
  Users,
  Award,
  Shield,
  HeartHandshake,
} from "lucide-react";
import { submitContactRequest, submitFeedback } from "../apis/settings";

const Settings = () => {
  const [activeTab, setActiveTab] = useState("contact");
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [feedbackForm, setFeedbackForm] = useState({
    name: "",
    email: "",
    rating: 0,
    feedback_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const companyInfo = {
    name: "Ping",
    phone: "+91 97277 89826",
    email: "sales@blutec.ai",
    address: "Ahmedabad, Gujarat India",
  };

  const stats = [
    {
      icon: Users,
      label: "Happy Customers",
      value: "10,000+",
      color: "text-sky-300",
    },
    {
      icon: Clock,
      label: "Response Time",
      value: "< 2 Hours",
      color: "text-emerald-300",
    },
    {
      icon: Award,
      label: "Satisfaction Rate",
      value: "98%",
      color: "text-amber-300",
    },
    {
      icon: Shield,
      label: "Data Security",
      value: "100%",
      color: "text-violet-300",
    },
  ];

  const feedbackTypes = [
    {
      value: "suggestion",
      label: "Suggestion",
      icon: Lightbulb,
      color: "text-sky-300",
      bgColor: "bg-white/5",
      borderColor: "border-white/10",
    },
    {
      value: "complaint",
      label: "Complaint",
      icon: AlertCircle,
      color: "text-rose-300",
      bgColor: "bg-white/5",
      borderColor: "border-white/10",
    },
    {
      value: "compliment",
      label: "Compliment",
      icon: ThumbsUp,
      color: "text-emerald-300",
      bgColor: "bg-white/5",
      borderColor: "border-white/10",
    },
    {
      value: "bug_report",
      label: "Bug Report",
      icon: Bug,
      color: "text-violet-300",
      bgColor: "bg-white/5",
      borderColor: "border-white/10",
    },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await submitContactRequest(authToken, contactForm);
      if (response.data.success) {
        toast.success(
          "Contact request submitted successfully! We'll get back to you soon."
        );
        setContactForm({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        toast.error(
          response.data.message || "Failed to submit contact request"
        );
      }
    } catch (error) {
      console.error("Contact submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await submitFeedback(authToken, feedbackForm);
      if (response.data.success) {
        toast.success(
          "Thank you for your feedback! Your input helps us improve."
        );
        setFeedbackForm({
          name: "",
          email: "",
          rating: 0,
          feedback_type: "",
          message: "",
        });
      } else {
        toast.error(response.data.message || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Feedback submission error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ rating, setRating }) => (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-6 h-6 cursor-pointer transition-all duration-200 hover:scale-110 ${
            star <= rating
              ? "text-amber-300 fill-current drop-shadow-sm"
              : "text-gray-500 hover:text-amber-200"
          }`}
          onClick={() => setRating(star)}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-gray-900 py-4 px-3">
      <div className="mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <div className="bg-gray-50 p-3 rounded-full shadow-lg ring-1 ring-gray-200">
              <HeartHandshake className="w-10 h-10 text-[#01CC54]" />
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-2 text-gray-900">Settings & Support</h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            We're here to help you succeed. Connect with our team or share your
            valuable feedback.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="rounded-2xl p-4 shadow-lg hover:shadow-xl transition-shadow bg-white border border-gray-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">
                      {stat.label}
                    </p>
                    <p className="text-xl font-bold text-gray-900 mt-0.5">
                      {stat.value}
                    </p>
                  </div>
                  <IconComponent className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className="bg-white rounded-2xl p-1.5 shadow-lg border border-gray-200">
            <button
              onClick={() => setActiveTab("contact")}
              className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 text-sm ${
                activeTab === "contact"
                  ? "bg-[#048AAD] text-white shadow-lg transform scale-105"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              <Phone className="w-3.5 h-3.5 inline mr-1.5" />
              Contact Us
            </button>
            <button
              onClick={() => setActiveTab("feedback")}
              className={`px-6 py-2 rounded-xl font-semibold transition-all duration-300 text-sm ${
                activeTab === "feedback"
                  ? "bg-[#048AAD] text-white shadow-lg transform scale-105"
                  : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />
              Feedback
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl shadow-xl p-4 h-fit border border-gray-200 bg-white">
              <h2 className="text-lg font-bold mb-4 text-center text-gray-900">
                Get in Touch
              </h2>

              <div className="space-y-4">
                <div className="flex items-center space-x-3 group">
                  <div className="p-2.5 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow ring-1 ring-gray-200 bg-gray-50">
                    <Phone className="w-5 h-5 text-[#01CC54]" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      Phone
                    </p>
                    <p className="font-semibold text-gray-900 text-sm">
                      {companyInfo.phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 group">
                  <div className="p-2.5 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow ring-1 ring-gray-200 bg-gray-50">
                    <Mail className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      Email
                    </p>
                    <p className="font-semibold text-gray-900 text-sm">
                      {companyInfo.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 group">
                  <div className="p-2.5 rounded-xl shadow-lg group-hover:shadow-xl transition-shadow ring-1 ring-gray-200 bg-gray-50">
                    <MapPin className="w-5 h-5 text-[#01CC54]" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      Address
                    </p>
                    <p className="font-semibold text-gray-900 text-sm">
                      {companyInfo.address}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                  <div className="flex items-start space-x-2.5">
                    <CheckCircle className="w-4 h-4 text-[#01CC54] mt-0.5" />
                    <p className="text-xs text-gray-700 leading-relaxed">
                      We're committed to providing exceptional support. Our team
                      typically responds within 2 hours during business hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Forms */}
          <div className="lg:col-span-2">
            {activeTab === "contact" ? (
              <div className="rounded-2xl shadow-xl p-4 border border-gray-200 bg-white">
                <h2 className="text-lg font-bold mb-4 text-gray-900">Contact Form</h2>

                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Your Name *"
                        value={contactForm.name}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                        required
                      />
                    </div>
                    <div className="relative">
                      <input
                        type="email"
                        placeholder="Your Email *"
                        value={contactForm.email}
                        onChange={(e) =>
                          setContactForm({
                            ...contactForm,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="tel"
                      placeholder="Phone Number (Optional)"
                      value={contactForm.phone}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          phone: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Subject *"
                      value={contactForm.subject}
                      onChange={(e) =>
                        setContactForm({
                          ...contactForm,
                          subject: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                      required
                    />
                  </div>

                  <textarea
                    placeholder="Your Message *"
                    rows="4"
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 resize-none text-sm"
                    required
                  ></textarea>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#048AAD] text-white py-2.5 px-6 rounded-xl font-semibold hover:bg-[#048AAD]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
                  >
                    <Send className="w-4 h-4" />
                    <span>{loading ? "Sending..." : "Send Message"}</span>
                  </button>
                </form>
              </div>
            ) : (
              <div className="rounded-2xl shadow-xl p-4 border border-gray-200 bg-white">
                <h2 className="text-lg font-bold mb-4 text-gray-900">Share Your Feedback</h2>

                <form onSubmit={handleFeedbackSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="Your Name *"
                      value={feedbackForm.name}
                      onChange={(e) =>
                        setFeedbackForm({
                          ...feedbackForm,
                          name: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Your Email *"
                      value={feedbackForm.email}
                      onChange={(e) =>
                        setFeedbackForm({
                          ...feedbackForm,
                          email: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 text-sm"
                      required
                    />
                  </div>

                  <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                    <label className="block text-sm font-semibold mb-3 text-gray-900">
                      Rate your experience (Optional)
                    </label>
                    <div className="flex justify-center">
                      <StarRating
                        rating={feedbackForm.rating}
                        setRating={(rating) =>
                          setFeedbackForm({ ...feedbackForm, rating })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-3 text-gray-900">
                      Feedback Type *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                      {feedbackTypes.map((type) => {
                        const IconComponent = type.icon;
                        const isActive =
                          feedbackForm.feedback_type === type.value;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() =>
                              setFeedbackForm({
                                ...feedbackForm,
                                feedback_type: type.value,
                              })
                            }
                            className={`p-3 border rounded-xl text-center transition-all duration-300 hover:shadow-lg transform hover:scale-105 ${
                              isActive
                                ? `border-gray-300 bg-gray-100 shadow-md scale-105`
                                : "border-gray-200 bg-gray-50"
                            }`}
                          >
                            <IconComponent
                              className={`w-5 h-5 mx-auto mb-1.5 ${type.color}`}
                            />
                            <p className="text-xs font-semibold text-gray-900">
                              {type.label}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <textarea
                    placeholder="Your Feedback *"
                    rows="4"
                    value={feedbackForm.message}
                    onChange={(e) =>
                      setFeedbackForm({
                        ...feedbackForm,
                        message: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#01CC54]/20 focus:border-[#01CC54] transition-all duration-300 resize-none text-sm"
                    required
                  ></textarea>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#048AAD] text-white py-2.5 px-6 rounded-xl font-semibold hover:bg-[#048AAD]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{loading ? "Submitting..." : "Submit Feedback"}</span>
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
